import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { hasUnreadActivity } from '@trello/business-logic/board';
import type { Preview } from '@trello/image-previews';
import type { RouterLinkProps } from '@trello/router/router-link';
import { RouterLink } from '@trello/router/router-link';
import type {
  BoardHeaderTestIds,
  WorkspaceNavigationTestIds,
} from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';
import { boardUrlFromShortLink } from '@trello/urls';

import { getBackgroundStyle } from './BoardTile';
import { useBoardTileLargeFragment } from './BoardTileLargeFragment.generated';
import { useBoardTileStarsFragment } from './BoardTileStarsFragment.generated';
import { StarredBoardButton } from './StarredBoardButton';

import styles from './NewBoardTileLarge.less';

interface NewBoardTileLargeProps extends Omit<RouterLinkProps, 'onClick'> {
  boardId: string;
  name: string;
  url: string;
  boardStarId?: string;
  onStarClick?: () => void;
  onBoardClick?: () => void;
  isTemplate?: boolean;
  backgroundBrightness?: 'dark' | 'light' | 'unknown' | null;
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundImageScaled?: Preview[];
  backgroundTile?: boolean;
  dateLastActivity?: string | null;
  dateLastView?: string | null;
  showStar?: boolean;
}

export const NewBoardTileLargeUnconnected: FunctionComponent<
  NewBoardTileLargeProps
> = ({
  boardId,
  name,
  url,
  className,
  isTemplate,
  backgroundBrightness,
  backgroundColor,
  backgroundImage,
  backgroundImageScaled,
  backgroundTile,
  boardStarId,
  dateLastActivity,
  dateLastView,
  onStarClick,
  onBoardClick,
  showStar,
  ...props
}) => {
  const tileBackgroundStyle = getBackgroundStyle(
    backgroundTile,
    backgroundImage,
    backgroundColor,
    backgroundImageScaled,
  );

  const intl = useIntl();

  const hasRecentActivity = hasUnreadActivity({
    dateLastActivity: dateLastActivity ? new Date(dateLastActivity) : undefined,
    dateLastView: dateLastView ? new Date(dateLastView) : undefined,
  });

  const ariaLabel = hasRecentActivity
    ? intl.formatMessage(
        {
          id: 'templates.workspace_navigation.recently-active',
          defaultMessage: '{name} (recently active)',
          description: 'Aria label for board tile',
        },
        { name },
      )
    : name;

  return (
    <div className={cx(styles.boardTile, className)}>
      <RouterLink
        href={url}
        title={name}
        onClick={onBoardClick}
        className={styles.link}
        aria-label={ariaLabel}
        {...props}
      >
        <div className={styles.boardDetails}>
          <div className={styles.background} style={tileBackgroundStyle}>
            {hasRecentActivity && (
              <i
                className={cx(styles.activityIndicator)}
                data-testid={getTestId<WorkspaceNavigationTestIds>(
                  'board-recent-activity-indicator',
                )}
              />
            )}
            <div
              className={cx(styles.badges, {
                [styles.dark]: backgroundBrightness !== 'light',
                [styles.light]: backgroundBrightness === 'light',
              })}
            >
              {isTemplate && (
                <div
                  className={styles.templateLozenge}
                  title={intl.formatMessage({
                    id: 'board template badge.templates are read-only boards for others to copy',
                    defaultMessage:
                      'Templates are read-only boards for others to copy.',
                    description: 'Title for template lozenge',
                  })}
                  data-testid={getTestId<BoardHeaderTestIds>('template-badge')}
                >
                  <FormattedMessage
                    id="board template badge.template"
                    defaultMessage="Template"
                    description="Text on template lozenge"
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.boardNameContainer}>
            <span className={styles.boardName}>{name}</span>
          </div>
        </div>
      </RouterLink>
      {showStar && (
        <div
          className={cx(styles.starContainer, {
            [styles.isStarred]: !!boardStarId,
          })}
        >
          <StarredBoardButton
            idBoard={boardId}
            boardStarId={boardStarId}
            maxBoardStarPosition={0}
            onClick={onStarClick}
            name={name}
            starColor={token('color.icon', '#FFFFFF')}
            isOnNewBoardTile
          />
        </div>
      )}
    </div>
  );
};

interface NewBoardTileLargeConnectedProps
  extends Omit<RouterLinkProps, 'onClick'> {
  boardId: string;
  onStarClick?: () => void;
  onBoardClick?: () => void;
  showStar?: boolean;
}

export const NewBoardTileLarge: FunctionComponent<
  NewBoardTileLargeConnectedProps
> = ({ boardId, onBoardClick, onStarClick, showStar = true, ...props }) => {
  const memberId = useMemberId();
  const { data: board } = useBoardTileLargeFragment({
    from: { id: boardId },
    returnPartialData: true,
  });
  const { data: member } = useBoardTileStarsFragment({
    from: { id: memberId },
  });

  if (!board) {
    return null;
  }

  const boardStarId =
    member && board.starred
      ? (member.boardStars ?? []).find((star) => star.idBoard === board.id)?.id
      : undefined;

  return (
    <NewBoardTileLargeUnconnected
      boardId={board.id}
      name={board.name}
      isTemplate={board.prefs?.isTemplate}
      backgroundBrightness={board.prefs?.backgroundBrightness}
      backgroundColor={board.prefs?.backgroundTopColor}
      backgroundImage={board.prefs?.backgroundImage ?? undefined}
      backgroundImageScaled={board.prefs?.backgroundImageScaled ?? undefined}
      backgroundTile={board.prefs?.backgroundTile}
      boardStarId={boardStarId}
      dateLastActivity={board.dateLastActivity}
      dateLastView={board.dateLastView}
      url={boardUrlFromShortLink(board.shortLink, board.name)}
      onBoardClick={onBoardClick}
      onStarClick={onStarClick}
      // Do not show star button if member is logged out
      showStar={showStar && isMemberLoggedIn()}
      {...props}
    />
  );
};
